import { createFileRoute } from "@tanstack/react-router";
import { ResourceArticle } from "@/components/ResourceArticle";
import { buildBreadcrumbs, buildArticle } from "@/components/SchemaJsonLd";
import heroImg from "@/assets/hero-v2.jpg";

export const Route = createFileRoute("/blog/best-garage-floor-coatings")({
  head: () => ({
    meta: [
      { title: "Best Garage Floor Coatings of 2026 | MLC Epoxy Floors" },
      { name: "description", content: "Our ranked guide to the garage floor coating systems that actually last in 2026 — polyaspartic, flake epoxy, metallic, and more." },
      { property: "og:title", content: "The Best Garage Floor Coatings of 2026" },
      { property: "og:description", content: "Ranked guide to floor coating systems that actually last." },
      { property: "og:url", content: "/blog/best-garage-floor-coatings" },
      { property: "og:image", content: heroImg },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: "/blog/best-garage-floor-coatings" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(buildArticle({
          title: "The Best Garage Floor Coatings of 2026",
          description: "Our ranked guide to the garage floor coating systems that actually last in 2026 — polyaspartic, flake epoxy, metallic, and more.",
          slug: "best-garage-floor-coatings",
          datePublished: "2026-03-12",
        })),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(buildBreadcrumbs([
          { name: "Home", href: "/" },
          { name: "Blog", href: "/blog" },
          { name: "Best Garage Floor Coatings of 2026", href: "/blog/best-garage-floor-coatings" },
        ])),
      },
    ],
  }),
  component: () => (
    <ResourceArticle
      eyebrow="Mar 12, 2026"
      title="The Best Garage Floor Coatings of 2026"
      intro="After installing over a thousand floors across the Calgary region, here's our honest ranking of the coating systems that actually deliver on their promises."
      image={heroImg}
      backHref="/blog"
      backLabel="All Articles"
      sections={[
        {
          type: "prose",
          heading: "How We Ranked These",
          body: "We evaluated each system on five criteria: longevity, UV stability, chemical resistance, install speed, and total cost of ownership over 15 years. Marketing claims were ignored — only field performance counted.",
        },
        {
          type: "list",
          heading: "The 2026 Rankings",
          items: [
            { label: "1. Hybrid epoxy + polyaspartic with flake", desc: "The gold standard. Pigmented epoxy base, broadcast vinyl flake, two polyaspartic top coats. 15–20 year lifespan, walk-on in hours." },
            { label: "2. Full polyaspartic one-day system", desc: "Slightly less build than the hybrid but installs in under 8 hours. Perfect for homeowners who can't lose the garage for multiple days." },
            { label: "3. Metallic epoxy with polyaspartic top", desc: "Stunning, three-dimensional look. Requires skilled application but performs as well as flake systems." },
            { label: "4. Grind & seal", desc: "Decorative polished concrete with a clear sealer. Beautiful in modern spaces but less impact-resistant than epoxy systems." },
            { label: "5. Solid color epoxy + polyaspartic", desc: "Clean industrial look. Shows every speck of dust but ideal for showrooms and commercial spaces." },
          ],
        },
        {
          type: "prose",
          heading: "Systems We No Longer Recommend",
          body: "Single-coat epoxy (no polyaspartic top): yellows within 18 months in any UV-exposed space. Water-based epoxy: too thin to bridge minor concrete defects, fails at the 5-year mark. Acrylic concrete sealers marketed as 'floor coatings': they're sealers, not coatings — peel within 2 years in a working garage.",
        },
        {
          type: "prose",
          heading: "The Takeaway",
          body: "For 90% of Calgary Region homeowners, the hybrid epoxy-plus-polyaspartic flake system is the right answer. It balances durability, beauty, and value better than anything else on the market today.",
        },
        {
          type: "links",
          heading: "Related On This Site",
          intro: "Explore the systems mentioned above and see them installed across the Calgary region.",
          links: [
            { href: "/services/flake-epoxy-flooring", label: "Flake Epoxy Flooring", desc: "Our flagship hybrid epoxy + polyaspartic flake system." },
            { href: "/services/polyaspartic-floor-coatings", label: "One-Day Polyaspartic Coatings", desc: "The full polyaspartic system ranked second above." },
            { href: "/gallery?category=Garage+Floors", label: "Garage Floor Gallery", desc: "See completed flake and polyaspartic garage installs." },
          ],
        },
      ]}
    />
  ),
});
